import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { BlueprintVersionDiff } from "./BlueprintVersionDiff";
import type { BlueprintVersion, CapabilityDimension, OutOfSyncArtefact } from "./blueprintTypes";

interface ThresholdRule {
  minScoreForCriticalDimensions: number | null;
  minOverallScore: number | null;
  minStrengthsCount: number | null;
}

interface Props {
  open: boolean;
  lastLockedVersion: BlueprintVersion | null;
  currentDimensions: CapabilityDimension[];
  lastLockedThresholdRule: ThresholdRule | null;
  currentThresholdRule: ThresholdRule | null;
  outOfSyncArtefacts: OutOfSyncArtefact[];
  // Server-side errors returned by the most recent submission (e.g. unmet
  // lock preconditions). Surfaced inline so the user sees what to fix
  // without losing the typed reason.
  errors?: string[];
  onClose: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
}

const REASON_MIN_LENGTH = 10;

const RULE_FIELDS: { key: keyof ThresholdRule; label: string }[] = [
  { key: "minScoreForCriticalDimensions", label: "Min score on critical dimensions" },
  { key: "minOverallScore", label: "Min overall score" },
  { key: "minStrengthsCount", label: "Min strengths count" },
];

function thresholdDiffEntries(
  oldRule: ThresholdRule | null,
  newRule: ThresholdRule | null,
): { label: string; oldValue: string; newValue: string }[] {
  if (!oldRule && !newRule) return [];
  const o = oldRule ?? {
    minScoreForCriticalDimensions: null,
    minOverallScore: null,
    minStrengthsCount: null,
  };
  const n = newRule ?? {
    minScoreForCriticalDimensions: null,
    minOverallScore: null,
    minStrengthsCount: null,
  };
  return RULE_FIELDS.flatMap(({ key, label }) => {
    const oldVal = String(o[key] ?? "");
    const newVal = String(n[key] ?? "");
    if (oldVal === newVal) return [];
    return [{ label, oldValue: oldVal, newValue: newVal }];
  });
}

export function RelockBlueprintModal({
  open,
  lastLockedVersion,
  currentDimensions,
  lastLockedThresholdRule,
  currentThresholdRule,
  outOfSyncArtefacts,
  errors = [],
  onClose,
  onConfirm,
}: Props) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const previewVersion: BlueprintVersion | null = useMemo(() => {
    if (!lastLockedVersion) return null;
    return {
      id: "preview",
      versionNumber: lastLockedVersion.versionNumber + 1,
      effectiveFrom: new Date().toISOString(),
      effectiveTo: null,
      changeSummary: null,
      capabilityDimensions: currentDimensions,
    };
  }, [lastLockedVersion, currentDimensions]);

  const thresholdChanges = useMemo(
    () => thresholdDiffEntries(lastLockedThresholdRule, currentThresholdRule),
    [lastLockedThresholdRule, currentThresholdRule],
  );

  if (!open) return null;

  const trimmed = reason.trim();
  const valid = trimmed.length >= REASON_MIN_LENGTH;

  const handleConfirm = async () => {
    if (busy || !valid) return;
    setBusy(true);
    try {
      await onConfirm(trimmed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="relock-blueprint-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <button
        type="button"
        aria-label="Close re-lock blueprint dialog"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onClose}
      />
      <div className="relative z-50 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-card p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 id="relock-blueprint-title" className="text-lg font-semibold text-text-primary">
              Re-lock blueprint
              {lastLockedVersion && (
                <span className="ml-2 text-sm font-normal text-text-faint">
                  v{lastLockedVersion.versionNumber} → v{lastLockedVersion.versionNumber + 1}
                </span>
              )}
            </h3>
            <p className="mt-2 text-sm text-text-muted">
              A new locked version will be created. The previous version remains queryable.
              Dependent artefacts pinned to the previous version will be flagged out of sync.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded p-1 text-text-faint hover:bg-card-hover"
          >
            <X size={16} />
          </button>
        </div>

        {lastLockedVersion && previewVersion && (
          <div className="mt-4 rounded border border-border p-3">
            <BlueprintVersionDiff oldVersion={lastLockedVersion} newVersion={previewVersion} />
          </div>
        )}

        {thresholdChanges.length > 0 && (
          <div className="mt-3 rounded border border-border p-3">
            <h4 className="mb-2 text-xs font-medium text-text-secondary">Threshold rule</h4>
            <div className="space-y-0.5 text-xs text-text-muted">
              {thresholdChanges.map((change) => (
                <div key={change.label}>
                  <span className="font-medium">{change.label}:</span>{" "}
                  <span className="bg-red-100 text-red-800 line-through dark:bg-red-900/30 dark:text-red-400">
                    {change.oldValue || "empty"}
                  </span>
                  {" → "}
                  <span className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                    {change.newValue || "empty"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {outOfSyncArtefacts.length > 0 && (
          <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
            <h4 className="mb-1 text-xs font-medium text-amber-900 dark:text-amber-300">
              Impact ({outOfSyncArtefacts.length}{" "}
              {outOfSyncArtefacts.length === 1 ? "artefact" : "artefacts"} will be flagged out of
              sync)
            </h4>
            <ul className="ml-4 list-disc text-xs text-amber-900 dark:text-amber-200">
              {outOfSyncArtefacts.map((a) => (
                <li key={a.id}>{a.name}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4">
          <label
            htmlFor="relock-reason"
            className="mb-1 block text-xs font-medium text-text-secondary"
          >
            Reason <span className="text-rose-500">*</span>
          </label>
          <textarea
            id="relock-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="What changed and why?"
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none"
          />
          <p className="mt-1 text-xs text-text-faint">
            {trimmed.length < REASON_MIN_LENGTH
              ? `At least ${REASON_MIN_LENGTH} characters (${trimmed.length}/${REASON_MIN_LENGTH})`
              : `${trimmed.length} characters`}
          </p>
        </div>

        {errors.length > 0 && (
          <div
            data-testid="relock-errors"
            role="alert"
            className="mt-3 rounded border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-700 dark:bg-rose-950 dark:text-rose-100"
          >
            <p className="mb-1 font-medium">Could not re-lock blueprint:</p>
            <ul className="ml-4 list-disc">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1.5 text-sm text-text-secondary hover:bg-card-hover"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !valid}
            className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Re-lock blueprint
          </button>
        </div>
      </div>
    </div>
  );
}
